import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Card } from '../../components/ui/Card';
import { LoadingPanel } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import { StageThemePicker } from './StageThemePicker';

interface Flag {
  key: string;
  label: string;
  description: string;
  /** What the switch itself says. */
  enabled: boolean;
  /** Whether the feature is actually live, which `requires` can override. */
  effective: boolean;
  /** Another flag that must also be on, or null. */
  requires: string | null;
  default: boolean;
  /** Whether anybody has decided, as opposed to the default applying. */
  decided: boolean;
}

/**
 * Feature flags.
 *
 * ## Why "off" and "not yet decided" look different
 *
 * A flag nobody has touched follows the catalogue's default; one somebody
 * explicitly turned off stays off even through a deploy that flips that
 * default. They are different states and the panel says so, because otherwise
 * the first person to look cannot tell whether the switch has ever been
 * considered.
 *
 * ## The switch is not the gate
 *
 * `RequireFeature` refuses the endpoints server-side. This changes what the
 * product *is*, not merely what it draws - which is why every flip is written
 * to the audit log. Turning one on ships a feature to everybody with no release
 * and no diff, so "when did this go live and who decided" has to be answerable
 * afterwards.
 */
export function AdminFeaturesPage() {
  const queryClient = useQueryClient();
  const refreshUser = useSession((s) => s.refreshUser);

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'features'],
    queryFn: () => api.get<{ features: Flag[] }>('/admin/features'),
  });

  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.post(`/admin/features/${key}`, { enabled }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'features'] });
      // The session carries the enabled list, and it decides what the rest of
      // the app draws - so the person who flipped it sees the effect without
      // reloading.
      await refreshUser();
    },
  });

  if (isPending || !data) return <LoadingPanel label="Reading the switches…" />;

  return (
    <div className="flex flex-col gap-5" data-admin-features>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Features</h1>
        <p className="mt-1 text-sm text-soft">
          Unfinished work lives on production behind these. Flipping one ships it to everybody,
          with no release. Every change is recorded in the audit log.
        </p>
      </div>

      {/*
        Beside the flags, because it is the same kind of act: one choice, made
        here, that every reader sees at once. See `StageThemePicker`.
      */}
      <StageThemePicker />

      <div className="flex flex-col gap-3">
        {data.features.map((flag) => (
          <Card key={flag.key} className="flex items-start justify-between gap-6 p-5">
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="text-sm font-semibold text-ink">{flag.label}</span>
                <code className="font-mono text-[10px] text-ghost">{flag.key}</code>
                {!flag.decided && (
                  <span className="meta-caps text-faint">default · nobody has decided</span>
                )}
                {/*
                  On, and still not live.

                  A dependent flag whose parent is off is switched on and
                  reaching nobody. Without saying so, turning it on appears to
                  do nothing at all, which is a genuinely baffling half hour.
                */}
                {flag.enabled && !flag.effective && flag.requires && (
                  <span className="meta-caps text-hot-text">held · needs {flag.requires}</span>
                )}
              </div>
              <p className="text-[13px] leading-relaxed text-soft">{flag.description}</p>

              {flag.requires && (
                <p className="meta-caps text-ghost">requires {flag.requires}</p>
              )}
            </div>

            <button
              type="button"
              role="switch"
              aria-checked={flag.enabled}
              aria-label={flag.label}
              data-feature={flag.key}
              disabled={toggle.isPending}
              onClick={() => toggle.mutate({ key: flag.key, enabled: !flag.enabled })}
              className={`mt-0.5 h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
                flag.enabled ? 'border-transparent bg-fresh' : 'border-line bg-raised'
              }`}
            >
              <span
                aria-hidden
                className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
                  flag.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
}
